import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { reportsAPI } from './reportsAPI';

// Thunks
export const fetchOfficeInvoices = createAsyncThunk(
  'reports/fetchOfficeInvoices',
  async (officeId, { rejectWithValue }) => {
    try {
      return await reportsAPI.getOfficeInvoices(officeId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOfficeClients = createAsyncThunk(
  'reports/fetchOfficeClients',
  async (officeId, { rejectWithValue }) => {
    try {
      return await reportsAPI.getOfficeFullClients(officeId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOfficeDevices = createAsyncThunk(
  'reports/fetchOfficeDevices',
  async (officeId, { rejectWithValue }) => {
    try {
      return await reportsAPI.getOfficeDevices(officeId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUniqueDevice = createAsyncThunk(
  'reports/fetchUniqueDevice',
  async (sno, { rejectWithValue }) => {
    try {
      return await reportsAPI.getUniqueDevice(sno);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEepromLogs = createAsyncThunk(
  'reports/fetchEepromLogs',
  async ({ cbid, installDate }, { rejectWithValue }) => {
    try {
      return await reportsAPI.getAllLogs5(cbid, installDate);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDealerUpdates = createAsyncThunk(
  'reports/fetchDealerUpdates',
  async (dealerId, { rejectWithValue }) => {
    try {
      return await reportsAPI.checkDealerUpdates(dealerId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  sales: {
    data: [],
    loading: false,
    error: null,
  },
  clients: {
    data: [],
    loading: false,
    error: null,
  },
  devices: {
    data: [],
    loading: false,
    error: null,
  },
  events: {
    deviceData: null,
    logsData: [],
    loading: false,
    error: null,
  },
  bug: {
    data: null,
    loading: false,
    error: null,
  }
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReportsData: (state) => {
      state.sales.data = [];
      state.clients.data = [];
      state.devices.data = [];
      state.events.deviceData = null;
      state.events.logsData = [];
      state.bug.data = null;
    }
  },
  extraReducers: (builder) => {
    // Sales
    builder
      .addCase(fetchOfficeInvoices.pending, (state) => {
        state.sales.loading = true;
        state.sales.error = null;
      })
      .addCase(fetchOfficeInvoices.fulfilled, (state, action) => {
        state.sales.loading = false;
        state.sales.data = action.payload;
      })
      .addCase(fetchOfficeInvoices.rejected, (state, action) => {
        state.sales.loading = false;
        state.sales.error = action.payload;
      });

    // Clients
    builder
      .addCase(fetchOfficeClients.pending, (state) => {
        state.clients.loading = true;
        state.clients.error = null;
      })
      .addCase(fetchOfficeClients.fulfilled, (state, action) => {
        state.clients.loading = false;
        state.clients.data = action.payload;
      })
      .addCase(fetchOfficeClients.rejected, (state, action) => {
        state.clients.loading = false;
        state.clients.error = action.payload;
      });

    // Devices
    builder
      .addCase(fetchOfficeDevices.pending, (state) => {
        state.devices.loading = true;
        state.devices.error = null;
      })
      .addCase(fetchOfficeDevices.fulfilled, (state, action) => {
        state.devices.loading = false;
        state.devices.data = action.payload;
      })
      .addCase(fetchOfficeDevices.rejected, (state, action) => {
        state.devices.loading = false;
        state.devices.error = action.payload;
      });

    // Unique Device
    builder
      .addCase(fetchUniqueDevice.pending, (state) => {
        state.events.loading = true;
        state.events.error = null;
      })
      .addCase(fetchUniqueDevice.fulfilled, (state, action) => {
        state.events.loading = false;
        state.events.deviceData = action.payload;
      })
      .addCase(fetchUniqueDevice.rejected, (state, action) => {
        state.events.loading = false;
        state.events.error = action.payload;
      });

    // EEPROM Logs
    builder
      .addCase(fetchEepromLogs.pending, (state) => {
        state.events.loading = true;
        state.events.error = null;
      })
      .addCase(fetchEepromLogs.fulfilled, (state, action) => {
        state.events.loading = false;
        state.events.logsData = action.payload;
      })
      .addCase(fetchEepromLogs.rejected, (state, action) => {
        state.events.loading = false;
        state.events.error = action.payload;
      });

    // Dealer Updates (Bugs)
    builder
      .addCase(fetchDealerUpdates.pending, (state) => {
        state.bug.loading = true;
        state.bug.error = null;
      })
      .addCase(fetchDealerUpdates.fulfilled, (state, action) => {
        state.bug.loading = false;
        state.bug.data = action.payload;
      })
      .addCase(fetchDealerUpdates.rejected, (state, action) => {
        state.bug.loading = false;
        state.bug.error = action.payload;
      });
  }
});

export const { clearReportsData } = reportsSlice.actions;
export default reportsSlice.reducer;
